import React, { ReactNode, useMemo } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Tooltip2 from 'components/Common/Tooltip2';
import Icon from 'components/Utils/NewIcon';
import DocumentUtils, { DocumentHistoryEntry } from 'services/DocumentUtils';
import { useAppSelector } from 'careand-redux/store';
import { PatientDocument, Patient } from 'generated/api';
import './style.scss';

type Props = {
  document: PatientDocument;
  patient?: Patient;
  width?: string;
  placement?: 'right' | 'bottom';
  // Custom hover trigger. Defaults to a circle-info icon when omitted.
  children?: ReactNode;
};

/**
 * The tooltip body on its own, mirroring AppointmentStatusHistoryContent, so it
 * can be dropped into any tooltip mechanism. One section per consecutive
 * (timestamp, actor) run — a single save writes several entries sharing
 * date/actor, and those read as one section listing its actions. Only
 * consecutive runs merge — array order is authoritative.
 */
export const DocumentStatusHistoryContent = ({ entries }: { entries: DocumentHistoryEntry[] }) => {
  const groups: { date: string; byWhom: string; entries: DocumentHistoryEntry[] }[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.date === entry.date && last.byWhom === entry.byWhom) last.entries.push(entry);
    else groups.push({ date: entry.date, byWhom: entry.byWhom, entries: [entry] });
  }

  return (
    <div className="document-status-history">
      {groups.map((group, index) => (
        <div className="dsh-entry" key={index}>
          <div className="dsh-entry-head">
            <span className="dsh-who">{group.byWhom}</span>
            <span className="dsh-date">{group.date}</span>
          </div>
          <div className="dsh-changes">
            {group.entries.map((entry, i) => (
              <span className="dsh-change" key={i}>
                <span className="dsh-change-label">
                  {entry.details.length ? `${entry.actionLabel}:` : entry.actionLabel}
                </span>
                {entry.details.map((detail, j) => (
                  <span className="dsh-change-value" key={j}>
                    {detail}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Hoverable trigger that renders a document's server-authored change history
 * (who did what, when) as a readable timeline inside a careand-tooltip.
 * Mirrors AppointmentStatusHistory. Staff-only: the backend strips
 * status_history from patient-role responses, and this renders nothing when
 * the trail is absent/empty (all documents predating the feature).
 */
const DocumentStatusHistory = ({
  document,
  patient,
  width = '20px',
  placement = 'right',
  children,
}: Props) => {
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const entries = useMemo(
    () => DocumentUtils.getStatusHistoryEntries(document, physicians, admins, patient),
    [document, physicians, admins, patient],
  );

  if (!entries.length) return null;

  return (
    <Tooltip2 placement={placement} tooltip={<DocumentStatusHistoryContent entries={entries} />}>
      {(register) => (
        <span className="dsh-trigger" {...register}>
          {children ?? (
            <IconButton className="ml-2">
              <Icon src="circle-info" height={width} width={width} />
            </IconButton>
          )}
        </span>
      )}
    </Tooltip2>
  );
};

export default DocumentStatusHistory;
